import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

/**
 * Workshop: Breadcrumb navigation path — project / space with trailing chevron.
 */

interface BreadcrumbSegment {
  label: string;
  onClick?: () => void;
}

interface BreadcrumbProps {
  segments: BreadcrumbSegment[];
  onChevronClick?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function Breadcrumb({
  segments,
  onChevronClick,
  className,
  "data-testid": testId,
}: BreadcrumbProps) {
  if (segments.length === 0) return null;

  return (
    <nav
      className={cn("flex h-7 items-center", className)}
      aria-label="Breadcrumb"
      data-testid={testId}
    >
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;

        return (
          <span key={index} className="flex items-center">
            {index > 0 && (
              <span className="px-1 text-[13px] text-muted" aria-hidden="true">
                /
              </span>
            )}
            {segment.onClick && !isLast ? (
              <button
                type="button"
                onClick={segment.onClick}
                className="text-[13px] text-secondary transition-colors hover:text-primary"
                data-testid={testId ? `${testId}-segment-${index}` : undefined}
              >
                {segment.label}
              </button>
            ) : (
              <span
                className={cn(
                  "text-[13px]",
                  isLast ? "font-medium text-primary" : "text-secondary",
                )}
                data-testid={testId ? `${testId}-segment-${index}` : undefined}
              >
                {segment.label}
              </span>
            )}
          </span>
        );
      })}

      <button
        type="button"
        onClick={onChevronClick}
        className="flex items-center pl-1"
        aria-label="Toggle space menu"
        data-testid={testId ? `${testId}-chevron` : undefined}
      >
        <Icons.ChevronDown className="h-2.5 w-2.5 text-muted" />
      </button>
    </nav>
  );
}

export type { BreadcrumbProps, BreadcrumbSegment };
